import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import Percent from "_shared/percentages/percent";
import { enqueueSnackbar } from "notistack";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import PercentRedlineInput from "work/proposals/redlining/field-redlines/_views/percent-redline-input";
import { Masked } from "work/_shared/masked-fields";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalDiscountContainer = styled("div")(({ theme }) => ({
  alignContent: "center",
  height: "100%",
  margin: theme.spacing(2, 0, 4, 0),
}));
const HighlightRedlinePercentInput = styled(PercentRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ active }) => ({
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const discountDescription =
  "You can share the AttorneyHub fees you incur from a project by establishing " +
  'an agreed upon "discount" from your vendor. 2.912% is the value to cover the cost.';

type DiscountTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  disableEditing?: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  commentThreads: CommentThread[];
  commentsToolTip: string;
  onCommentsClicked: (field: ProposalField, title?: string) => void;
};

const DiscountTab = (props: Readonly<DiscountTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    commentThreads,
    commentsToolTip,
    onCommentsClicked,
  } = props;

  if (Masked.isMasked(proposalRedline.discount)) return "You do not have permission to view or edit the discount.";

  return (
    proposalRedline && (
      <ProposalDiscountContainer>
        <HighlightRedlinePercentInput
          focused={activeReviewField?.name === ProposalFieldName.Discount}
          active={activeReviewField?.isEqualTo(ProposalField.Discount)}
          readOnly={disableEditing}
          disabled={disableEditing}
          originalPercentFieldRedline={proposalRedline.discount}
          onCommentsClicked={() => onCommentsClicked(ProposalField.Discount)}
          commentsToolTip={commentsToolTip}
          commentThreads={commentThreads}
          onPercentFieldRedlineChange={(
            updatedRedline: FieldRedline<Percent>,
            traversalFieldOverride?: ProposalField | null
          ) => {
            try {
              onProposalRedlineChange(proposalRedline.updateDiscountRedline(updatedRedline), traversalFieldOverride);
            } catch (error) {
              console.error("Error updating discount", error);
              enqueueSnackbar("Error updating discount", { variant: "error" });
            }
          }}
          onTraverseIn={() => {
            onTraverseToNewField(ProposalField.Discount);
          }}
        />
        <HelperText>
          A discount of <b>3%</b> will cover subscription costs.
          <Tooltip disableFocusListener title={discountDescription} placement="right" arrow>
            <IconButton size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </HelperText>
      </ProposalDiscountContainer>
    )
  );
};

export default DiscountTab;
